'use client';

import { useEffect, useState } from 'react';

/**
 * StagedArt — a piece of scene art with its intro (TT-1046, art team note
 * 2), one `reveal` descriptor from sceneAssets. The manifest may carry `stages`: a few earlier moments of the same
 * scene (the mound the mountain rises from, the sapling the tree grows
 * from), generated as one storyboard so they share a composition. Each
 * frame is the finished image's full frame, so they stack pixel for pixel.
 * Or it may carry `intro`: a short clip of the piece un-drawing itself,
 * played in reverse (Sora, white ground — it multiplies onto the paper),
 * which runs once and crossfades to the finished piece. A clip wins over
 * stages, and one that never ends (a hidden tab defers media loading)
 * settles after INTRO_MAX_MS anyway.
 *
 * The stage intro is pure CSS (styles/tapestryAnimations.css): every stage
 * runs the same flip keyframe with its own delay, the finished piece fades
 * in last and stays. All play once, at mount; a `.scene-instant` ancestor
 * (a static preview) or a reduced-motion preference shows the finished
 * piece only. Keep the step in sync with `--scene-stage-step` in the
 * stylesheet.
 */

const STEP_MS = 750;
const INTRO_MAX_MS = 8000;

const FINAL_CLASS = {
  clip: 'scene-final scene-final-after-video',
  stages: 'scene-final',
};

/**
 * `reveal` is the piece's intro descriptor from sceneAssets (one home for
 * the precedence clip > stages): `{ kind: 'clip', src }`,
 * `{ kind: 'stages', urls }`, or null — a piece with neither fades in
 * (TT-1163: a reveal that is not real growth does more harm than good).
 */
export default function StagedArt({ src, reveal = null, opacity = 1, className, style, sid }) {
  const mode = reveal?.kind || 'stages';
  const stages = mode === 'stages' ? reveal?.urls || [] : [];
  const [videoDone, setVideoDone] = useState(false);
  const settle = () => setVideoDone(true);
  useEffect(() => {
    if (mode !== 'clip') return undefined;
    const id = setTimeout(() => setVideoDone(true), INTRO_MAX_MS);
    return () => clearTimeout(id);
  }, [mode]);
  const finalClass = mode === 'clip' && !videoDone ? `${FINAL_CLASS.clip} scene-final-pending` : FINAL_CLASS[mode];
  return (
    <div className={className} data-sid={sid} style={{ position: 'absolute', pointerEvents: 'none', opacity, ...style }}>
      {mode === 'clip' && (
        <video
          src={reveal.src}
          className={videoDone ? 'scene-video scene-video-done' : 'scene-video'}
          // React sets `muted` as a property after mount; the attribute must be there for autoplay
          ref={(v) => { if (v) v.muted = true; }}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={settle}
          onError={settle}
        />
      )}
      {stages.map((url, k) => (
        // eslint-disable-next-line -- composited scene art frames (no-img-element; named rules break tapp's CRA lint)
        <img key={url} src={url} alt="" draggable={false} className="scene-stage" style={{ animationDelay: `${k * STEP_MS}ms` }} />
      ))}
      {/* eslint-disable-next-line -- the finished piece (no-img-element; named rules break tapp's CRA lint) */}
      <img
        src={src}
        alt=""
        draggable={false}
        className={finalClass}
        style={mode === 'stages' ? { animationDelay: `${stages.length * STEP_MS}ms` } : undefined}
      />
    </div>
  );
}
